import {create} from "zustand/react";
import {createJSONStorage, persist} from "zustand/middleware";
import runAuthRequest from "../../../redux/util/runAuthRequest";
import getCartAction from "./getCartAction";
import syncCartLineAction from "./syncCartLineAction";
import updateOrderAction from "./updateOrderAction";
import setSelectedLocation from "./setSelectedLocationAction";
import setDeliveryMethod from "./setDeliveryMethodAction";
import setDeliveryDateAction from "./setDeliveryDateAction";
import prepareCheckout from "./prepareCheckoutAction";
import setPaymentTypeAction from "./setPaymentTypeAction";
import completeOrderAction from "./completeOrderAction";
import prepareOrderAction from "./prepareOrderAction";
import setCardAction from "./setCardAction";
import reopenOrderAction from "./reopenOrderAction";
import fetchInstitutionsAction from "./fetchInstitutionsAction";

export const getLineKey = (olID) => 'addToCart_' + olID;
const getLineKeyForCartRecord = (cartRecord) => {
    if(cartRecord.orderLineID > 0) {
        return getLineKey(cartRecord.orderLineID);
    } else {
        return getLineKey('p' + cartRecord.productID);
    }
}

const storeOptions = {
    name: 'checkout-store',
    storage: createJSONStorage(() => sessionStorage),
}

const handleAsync = async (set, key, req, resultMap=(x,y)=>y) => {
    set(state => ({ loading: { ...state.loading, [key]: true }, error: { ...state.error, [key]: null } }));
    try {
        const result = await req();

        set((state) => ({
            ...resultMap(state,result),
            loading: { ...state.loading, [key]: false },
        }));
    } catch (e) {
        set((state) => ({
            error: { ...state.error, [key]: e.message || e },
            loading: { ...state.loading, [key]: false },
        }));
    }
}

const handleAsyncAuth = (session, set, key, req, resultMap=(x,y)=>y) => {
    const authReq = () => runAuthRequest(session.sessionData, req, session.dispatch);
    void handleAsync(set, key, authReq, resultMap);
}

const store = (set, get) => ({
    loading: {},
    error: {},
    input: {},

    orderID: 0,
    paymentTransactionId: null,

    clearCart: () => set({ orderID: undefined, order: undefined, products: undefined, charges: undefined, paymentTransactionId: null }),

    confirmOrder: (order) => set((state) => {
        const completedOrder = { ...(order ?? state.order) };
        completedOrder.products = state.products;
        completedOrder.charges = state.charges;
        return { orderID: 0, order: undefined, products: undefined, charges: undefined, completedOrder, input: undefined, paymentTransactionId: null };
    }),
    clearConfirmation: () => set({ completedOrder: undefined }),

    completeIt: (session) => handleAsyncAuth(session, set, 'complete', (s) => completeOrderAction(s, get)),
    prepareIt: (session, afterPrepare) => handleAsyncAuth(session, set, 'cart', (s) => prepareOrderAction(get, s, afterPrepare)),

    addToCart: (session, cartRecord) => handleAsyncAuth(
        session,
        set,
        getLineKeyForCartRecord(cartRecord),
        s => syncCartLineAction(s, cartRecord, set, get)
    ),
    prepareCheckout: (session) => handleAsyncAuth(session, set, 'prepareCheckout', (s) => prepareCheckout(s, set, get)),
    updateOrder: (session, field, val) => handleAsyncAuth(session, set, 'cart', (s) => updateOrderAction(s, get, field, val)),
    getCart: (session, createNew) => handleAsyncAuth(session, set, 'cart', (s) => getCartAction(s,createNew)),

    clearInput: (field) => set({input: {...get().input, [field]: undefined}}),
    setSelectedLocation: (session, selected) => handleAsyncAuth(session, set, 'cart', s => setSelectedLocation(get, s, selected)),
    setDeliveryMethod: (session, selected) => handleAsyncAuth(session, set, 'cart', s => setDeliveryMethod(get, s, selected)),
    setDeliveryDate: (session, selected) => handleAsyncAuth(session, set, 'cart', s => setDeliveryDateAction(get, s, selected)),
    setPaymentMethod: (session, selected) => {
        handleAsyncAuth(session, set, 'cart', s => setPaymentTypeAction(get, s, selected));
        if (selected === 'O' && !get().institutions && !get().loading?.institutions) {
            handleAsyncAuth(session, set, 'institutions', s => fetchInstitutionsAction(s), (state, result) => ({ institutions: result }));
        }
    },
    setCard: (session, selected) => handleAsyncAuth(session, set, 'cart', s => setCardAction(s, get, selected)),
    getOverpaymentForTotal: (total) => {
        const orderFraction = total * (0.15);
        const overPayment = Math.min(orderFraction, get().paymentDetails?.overDue);
        return parseFloat(overPayment.toFixed(2)) ?? 0;
    },
    getOverpayment: () => get().getOverpaymentForTotal(get().order.GrandTotal),
    getSubTotalDue: () => {
        return Math.max(((get().order?.GrandTotal + get().getOverpayment()) - get().paymentDetails?.previousPayment), 0);
    },
    getCreditCardChargeRate: () => get().input?.card?.charge_rate ?? 0,
    getCreditCardCharge: () => (get().getSubTotalDue() * (get().getCreditCardChargeRate()/100)),
    getTotalDue: () => get().getSubTotalDue() + get().getCreditCardCharge(),

    setInstitution: (institutionId) => set(state => ({ input: { ...state.input, institutionId } })),
    setPaymentTransactionId: (id) => set({ paymentTransactionId: id }),

    amendOrder: (session, orderID, onReopened) => handleAsyncAuth(session, set, 'amend', s => reopenOrderAction(s, orderID, onReopened)),

})

const useCheckoutStore = create(
    persist(
        store,
        storeOptions
    )
)

export default useCheckoutStore;